import React, { useEffect, useState, useRef, useImperativeHandle, useMemo } from 'react';
import {
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  Select,
  Drawer,
  Popconfirm,
  message,
  Tooltip,
} from '@drip/drip-design';
import { PlusOutlined, DownloadOutlined } from '@drip/drip-design-icons';
import Container from '@/components/container';
import marketingPlanDel from '@/apis/marketingPlan/del';
import marketingPlanList from '@/apis/marketingPlan/list';
import marketingPlanSubscribe from '@/apis/marketingPlan/subscribe';
import SalesPlanDrawer from './components/sales-plan-drawer';
import marketingPlanBatchDonwload from '@/apis/marketingPlan/batchDownload';
import queryString from 'query-string';
import CommonLog from '@/components/component-log';
import history from '@/history';

const { Title } = Typography;

export default function ProductManagePanel() {
  const [conditions, setConditions] = useState({ pageSize: 10, currentPage: 1 });
  const [preDownload, setPreDownload] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [data, setData] = useState({ loading: true, currentPage: 1, pageSize: 10, totalCount: 1 });

  const parsedQuery = useMemo(() => {
    return queryString.parse(location.search);
  }, []);

  useEffect(() => {
    if (parsedQuery.create) {
      return openDrawer.new();
    }

    if (parsedQuery.code && parsedQuery.editable) {
      return openDrawer.edit(parsedQuery.code);
    }

    if (parsedQuery.code) {
      return openDrawer.view(parsedQuery.code);
    }
    // eslint-disable-next-line
  }, [parsedQuery]);

  useEffect(() => {
    setData((innerData) => ({ ...innerData, loading: true }));
    marketingPlanList(conditions).then((res) => {
      if (res.ret === '0') {
        setData({ ...res.data, loading: false });
      } else {
        message.error('网络异常请稍后重试');
      }
    });
  }, [conditions]);
  const drawerRef = useRef(null);

  const columns = [
    {
      title: '序号',
      key: 'name',
      width: 60,
      render: (_, record, i) => i + 1 + data.pageSize * (data.currentPage - 1),
    },
    {
      title: '计划名称',
      dataIndex: 'name',
      width: '300px',
      render(name) {
        return (
          <Tooltip title={name}>
            <span className="ellipsis-1-line">{name}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      render(status) {
        return ['-', '进行中', '未开始', '已结束', '草稿'][status] || '-';
      },
    },
    {
      title: '总目标',
      dataIndex: 'total',
      render: (total, { tags }) => total,
    },
    {
      title: '已完成',
      dataIndex: 'complete',
      render: (complete, { tags }) => complete,
    },
    {
      dataIndex: 'completeRate',
      title: '达成率',
      render: (completeRate, record) => {
        return completeRate || '-';
      },
    },
    {
      title: '操作',
      width: 300,
      dataIndex: 'btn',
      render: (btns = [], record) => {
        const someBtns = {
          detail() {
            return (
              <CommonLog>
                <a clstag="yx_center_hzcp_1678258944439|21" onClick={() => openDrawer.view(record.code)}>
                  查看
                </a>
              </CommonLog>
            );
          },
          update() {
            return (
              <CommonLog>
                <a clstag="yx_center_hzcp_1678258944439|22" onClick={() => openDrawer.edit(record.code)}>
                  编辑
                </a>
              </CommonLog>
            );
          },
          del() {
            return (
              <Popconfirm
                placement="top"
                title="确认删除吗？"
                onConfirm={() => {
                  marketingPlanDel({ marketingPlanCode: record.code }).then((res) => {
                    if (res.ret === '0') {
                      updateList();
                    } else {
                      message.error(res.msg);
                    }
                  });
                }}
                okText="确定"
                cancelText="取消"
              >
                <a>删除</a>
              </Popconfirm>
            );
          },
          download() {
            return (
              <CommonLog>
                <a
                  clstag="yx_center_hzcp_1678258944439|24"
                  onClick={() => {
                    downloadModalInstance.current.show(record.code);
                  }}
                >
                  下载
                </a>
              </CommonLog>
            );
          },
          unSubscription() {
            return (
              <a
                onClick={() => {
                  marketingPlanSubscribe({ type: 2, marketingPlanCode: record.code }).then((res) => {
                    if (res.ret === '0') {
                      message.success('取消订阅成功');
                      updateList();
                    } else {
                      message.success('取消订阅失败请重试');
                    }
                  });
                }}
              >
                取消订阅
              </a>
            );
          },
          subscription() {
            return (
              <CommonLog>
                <a
                  onClick={() => {
                    marketingPlanSubscribe({ type: 1, marketingPlanCode: record.code }).then((res) => {
                      if (res.ret === '0') {
                        message.success('订阅成功');
                        updateList();
                      } else {
                        message.success('订阅失败请重试');
                      }
                    });
                  }}
                  clstag="yx_center_hzcp_1678258944439|23"
                >
                  订阅
                </a>
              </CommonLog>
            );
          },
        };
        return <Space>{btns.map((btn) => someBtns[btn]?.()).filter((item) => !!item)}</Space>;
      },
    },
  ];

  const list = data.list || [];

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !record?.btn.includes('download'), // Column configuration not to be checked
    }),
  };

  function updateList() {
    setConditions((condtion) => ({ ...condtion }));
  }

  const openDrawer = {
    new: () => {
      setShowEditDrawer(true);
      drawerRef.current.getOpenStatus({ status: 'new' });
    },
    edit: (id) => {
      setShowEditDrawer(true);
      drawerRef.current.getOpenStatus({ status: 'edit', id: id });
    },
    view: (id) => {
      setShowEditDrawer(true);
      drawerRef.current.getOpenStatus({ status: 'view', id: id });
    },
  };

  const downloadModalInstance = useRef(null);

  function onPreDownload() {
    setPreDownload(true);
  }

  function onDownload() {
    downloadModalInstance.current.show(selectedRowKeys.join(','));
  }

  function onCancelPreDownload() {
    setPreDownload(false);
    setSelectedRowKeys([]);
  }

  function onOkDownload() {
    // 等弹窗关闭后再清除
    setPreDownload(false);
    setSelectedRowKeys([]);
    message.success('下载成功，文件稍后通过鲸盘进行推送', 5);
  }

  return (
    <Container>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <Title level={3}>销售计划管理</Title>
        <BtnsOfRight
          onPreDownload={onPreDownload}
          preDownload={preDownload}
          onCancelPreDownload={onCancelPreDownload}
          onDownload={onDownload}
          selectedRowKeys={selectedRowKeys}
          onOkDownloading={() => setDownloading(true)}
          onCreatePlan={() => openDrawer.new()}
        />
      </div>
      <Table
        onChange={(pagination) => {
          // 重置掉选择的下拉框
          onCancelPreDownload();
          const { current, pageSize } = pagination;
          setConditions((innerConditions) => {
            return { ...innerConditions, currentPage: current, pageSize };
          });
        }}
        pagination={{ pageSize: data.pageSize, current: data.currentPage, total: data.totalCount }}
        loading={data.loading}
        rowKey="code"
        rowSelection={preDownload ? rowSelection : undefined}
        columns={columns}
        dataSource={list}
      />
      <SalesPlanDrawer
        ref={drawerRef}
        onClose={() => {
          setShowEditDrawer(false);
          updateList();
          history.replace(location.pathname);
        }}
        visible={showEditDrawer}
        onDownLoad={(id) => downloadModalInstance.current.show(id)}
      />
      <ModalOfDownloadPlan ref={downloadModalInstance} onOkDownload={onOkDownload} onCancel={onCancelPreDownload} />
    </Container>
  );
}

export const ModalOfDownloadPlan = React.forwardRef(function ModalOfDownloadPlan(
  { onCancel = () => {}, onOkDownload },
  ref
) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  // 需要下载的组id 1,2,3 这种形式传参
  const marketingPlanCodes = useRef();

  useImperativeHandle(ref, () => ({
    show(planCodes) {
      marketingPlanCodes.current = planCodes;
      setVisible(true);
    },
  }));

  function handleCancel() {
    marketingPlanCodes.current = '';
    setVisible(false);
    onCancel();
  }

  function onOk() {
    marketingPlanCodes.current = '';
    setVisible(false);
    onOkDownload?.();
  }

  return (
    <Modal onOk={() => form.submit()} title="下载数据" onCancel={handleCancel} visible={visible} centered={true}>
      <Form
        onFinish={(val) => {
          marketingPlanBatchDonwload({ ...val, marketingPlanCodes: marketingPlanCodes.current }).then((res) => {
            if (res?.ret === '0') {
              onOk();
            } else {
              message.error('下载失败请重试');
            }
          });
        }}
        form={form}
      >
        <Form.Item name="type" label="数据选择" required rules={[{ required: true, message: '请选择数据' }]}>
          <Select placeholder="请选择">
            <Select.Option value="1">销售计划下载</Select.Option>
            <Select.Option value="2">计划明细下载</Select.Option>
            <Select.Option value="3">sku明细下载</Select.Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
});

// 右侧的下载和新建按钮
function BtnsOfRight({ onPreDownload, onDownload, preDownload, onCancelPreDownload, selectedRowKeys, onCreatePlan }) {
  if (preDownload)
    return (
      <>
        <Space>
          <Button onClick={onDownload} disabled={!selectedRowKeys.length} type="primary">
            <DownloadOutlined />
            下载数据
          </Button>
          <Button onClick={() => onCancelPreDownload(false)} type="primary" ghost>
            取消
          </Button>
        </Space>
      </>
    );

  return (
    <Space>
      <CommonLog>
        <Button clstag="yx_center_hzcp_1678258944439|25" onClick={onPreDownload} type="primary" ghost>
          批量下载数据
        </Button>
      </CommonLog>
      <CommonLog>
        <Button clstag="yx_center_hzcp_1678258944439|20" type="primary" onClick={() => onCreatePlan()}>
          <PlusOutlined />
          新建计划
        </Button>
      </CommonLog>
    </Space>
  );
}
